import { useLayoutEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { useAuth } from '@/context/AuthContext';
import { getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';
import { isDesktopOffline } from '@/api/desktopSync';
import { API } from '@/config/constants';
import { resolveBackendUrl } from '@/config/desktopBootstrap';
import {
  getSources, getSendingFlorists, getSubsourceMappings, getSourceMappings,
  createSource, updateSource, deleteSource, createSendingFlorist, updateSendingFlorist,
  deleteSendingFlorist, updateSubsourceMapping, deleteSubsourceMapping,
  updateSourceMapping, deleteSourceMapping,
} from '@/api/sources';

// In-memory obligations survive editor/account replacement for the affected
// tenant and endpoint. They contain no credentials or draft payloads and are
// neither a durable receipt nor a server/cross-window lock.
const resources = new Map();
let sequence = 0;
const resourceFor = scope => {
  if (!resources.has(scope)) resources.set(scope, { epoch: 0, write: null, observers: new Set() });
  return resources.get(scope);
};
const connection = () => JSON.stringify([window.lvbDesktop?.backendUrl, window.lvbDesktop?.localBackendUrl]);
// The adapters use the module-initialized API, not the latest mutable bridge.
// A contradictory connection requires a reload; it cannot create a second gate
// while still sending requests to the old transport destination.
const transportMatches = () => {
  const backend = resolveBackendUrl(window.location.protocol, window.lvbDesktop, process.env.REACT_APP_BACKEND_URL);
  return API !== null && backend !== null && `${backend || ''}/api` === API;
};
const identity = binding => JSON.stringify([binding?.user?.tenant_id, binding?.user?.id, binding?.source]);
const text = value => typeof value === 'string' && !!value;
const optionalText = value => value == null || typeof value === 'string';
const count = value => Number.isSafeInteger(value) && value >= 0;
const sourceRow = row => !!row && text(row.id) && text(row.name)
  && optionalText(row.url) && optionalText(row.product_url);
const floristRow = row => sourceRow(row)
  && (row.network_sources === undefined || (Array.isArray(row.network_sources) && row.network_sources.every(text)))
  && (row.owns_catalog === undefined || typeof row.owns_catalog === 'boolean');
const mappingRow = field => row => !!row && text(row.id) && typeof row.extracted_string === 'string'
  && (row[field] == null || text(row[field])) && count(row.order_count)
  && optionalText(row.source_name) && optionalText(row.sending_florist_name);
const list = (rows, valid) => Array.isArray(rows) && rows.every(valid)
  && new Set(rows.map(row => row.id)).size === rows.length;
const validData = data => list(data.sources, sourceRow) && list(data.florists, floristRow)
  && list(data.mappings, mappingRow('sending_florist_id')) && list(data.sourceMappings, mappingRow('source_id'));
const equalStrings = (left, right) => Array.isArray(left) && Array.isArray(right)
  && left.length === right.length && left.every((value, index) => value === right[index]);
// The server stores rules in a canonical form, so the saved rows need not
// match the sent ones key for key. What must hold is that the same windows
// came back with the same number of charges: enough to prove the write landed
// without asserting a normalization the client does not perform.
const savedRuleSets = (rows, sent) => Array.isArray(rows) && Array.isArray(sent)
  && rows.length === sent.length
  && equalStrings(
    rows.map(row => `${row?.start_date}:${(row?.rules || []).length}`).sort(),
    sent.map(row => `${row?.start_date}:${(row?.rules || []).length}`).sort(),
  );
const matchesDirectory = (row, payload, florist) => (florist ? floristRow(row) : sourceRow(row))
  && (!('name' in payload) || row.name === payload.name.trim())
  && (!('url' in payload) || (row.url ?? null) === (payload.url?.trim() || null))
  && (!('product_url' in payload) || (florist ? row.product_url : row.product_url ?? null) === (payload.product_url?.trim() || (florist ? '' : null)))
  && (!('network_sources' in payload) || equalStrings(row.network_sources, payload.network_sources))
  && (!('owns_catalog' in payload) || row.owns_catalog === payload.owns_catalog)
  && (!('deduction_rule_sets' in payload) || savedRuleSets(row.deduction_rule_sets, payload.deduction_rule_sets));

const operations = {
  'source:create': { collection: 'sources', execute: (_, payload, config) => createSource(payload, config), valid: (value, _, payload) => matchesDirectory(value, payload, false) },
  'source:update': { collection: 'sources', execute: updateSource, valid: (value, id, payload) => value?.id === id && matchesDirectory(value, payload, false) },
  'source:delete': { collection: 'sources', execute: deleteSource, valid: (value, _, payload) => ['Source deleted', 'Source already deleted'].includes(value?.message)
    && value.mode === (payload?.mode || 'delete') && ['orders_updated', 'mappings_updated', 'products_updated', 'florists_updated'].every(field => count(value.summary?.[field])) },
  'florist:create': { collection: 'florists', execute: (_, payload, config) => createSendingFlorist(payload, config), valid: (value, _, payload) => matchesDirectory(value, payload, true) },
  'florist:update': { collection: 'florists', execute: updateSendingFlorist, valid: (value, id, payload) => value?.id === id && matchesDirectory(value, payload, true) },
  'florist:delete': { collection: 'florists', execute: (id, _, config) => deleteSendingFlorist(id, config), valid: value => ['Deleted', 'Already deleted'].includes(value?.message) && count(value.mappings_released) },
  'mapping:update': { collection: 'mappings', execute: (id, payload, config) => updateSubsourceMapping(id, payload.sending_florist_id, config), valid: value => value?.message === 'Mapping updated' },
  'mapping:delete': { collection: 'mappings', execute: (id, _, config) => deleteSubsourceMapping(id, config), valid: value => value?.message === 'Mapping deleted' },
  'sourceMapping:update': { collection: 'sourceMappings', execute: (id, payload, config) => updateSourceMapping(id, payload.source_id, config), valid: value => value?.message === 'Mapping updated' },
  'sourceMapping:delete': { collection: 'sourceMappings', execute: (id, _, config) => deleteSourceMapping(id, config), valid: value => value?.message === 'Mapping deleted' },
};
const retiredRead = () => Object.assign(new Error('Sources observation retired'), { code: 'sources_read_retired' });

export function createSourcesCatalogController(authorize, scope) {
  const resource = resourceFor(scope);
  const owner = {};
  const key = ++sequence;
  const listeners = new Set();
  const reads = new Set();
  let active = false;
  let lifecycle = 0;
  let version = 0;
  let observedEpoch = -1;
  let catalogRequest = null;
  let snapshot = { data: null, state: 'loading', notice: '', active: false, pending: false, unknown: false, reviewable: false, reviewRecord: null };
  const owns = () => active && !!authorize();
  const canReview = () => owns() && resource.write?.status === 'unknown'
    && !resource.write.kind.endsWith(':create') && snapshot.state === 'available'
    && observedEpoch === resource.epoch
    && !!snapshot.data?.[operations[resource.write.kind].collection].some(row => row.id === resource.write.id);
  const notify = () => {
    const reviewable = canReview();
    const reviewRecord = reviewable ? { kind: resource.write.kind, id: resource.write.id,
      failure: resource.write.failure || null, submitted: resource.write.submitted ?? null,
      value: snapshot.data[operations[resource.write.kind].collection].find(row => row.id === resource.write.id) } : null;
    snapshot = { ...snapshot, active: owns(), pending: resource.write?.status === 'pending', unknown: resource.write?.status === 'unknown', reviewable, reviewRecord };
    listeners.forEach(listener => listener());
  };
  const patch = update => { snapshot = { ...snapshot, ...update }; notify(); };
  const invalidate = () => {
    ++version;
    observedEpoch = -1;
    reads.forEach(request => request.abort());
    catalogRequest = null;
  };
  const read = async (execute, { isCurrent = () => true, signal } = {}) => {
    const config = owns() && authorize();
    if (!config || resource.write?.status === 'pending' || !isCurrent() || signal?.aborted) throw retiredRead();
    const epoch = resource.epoch;
    const life = lifecycle;
    const request = new AbortController();
    const cancel = () => request.abort();
    signal?.addEventListener('abort', cancel, { once: true });
    reads.add(request);
    const current = () => owns() && life === lifecycle && epoch === resource.epoch && !request.signal.aborted && isCurrent();
    try {
      // Capture credentials now, but check ownership again at deferred dispatch.
      await Promise.resolve();
      if (!current()) throw retiredRead();
      const result = await execute({ ...config, signal: request.signal });
      if (!current()) throw retiredRead();
      return result;
    } finally {
      reads.delete(request);
      signal?.removeEventListener('abort', cancel);
    }
  };
  const refresh = async () => {
    if (!owns() || resource.write?.status === 'pending') return false;
    catalogRequest?.abort();
    const request = new AbortController();
    catalogRequest = request;
    const requested = ++version;
    const epoch = resource.epoch;
    const life = lifecycle;
    const current = () => owns() && requested === version && epoch === resource.epoch && life === lifecycle;
    observedEpoch = -1;
    patch({ state: 'loading' });
    try {
      const [sources, florists, mappings, sourceMappings] = await read(config => Promise.all([
        getSources(config), getSendingFlorists(config), getSubsourceMappings(config), getSourceMappings(config),
      ]), { isCurrent: current, signal: request.signal });
      const data = { sources, florists, mappings, sourceMappings };
      if (!validData(data)) throw new Error('Invalid Sources catalog');
      if (!current()) return false;
      observedEpoch = epoch;
      patch({ data, state: 'available' });
      return true;
    } catch {
      if (current()) patch({ state: 'unavailable' });
      return false;
    } finally {
      if (catalogRequest === request) catalogRequest = null;
    }
  };
  const changed = (origin, settled) => {
    if (origin === owner) { notify(); return; }
    invalidate();
    if (owns()) {
      patch({ state: 'unavailable' });
      if (settled && resource.write?.status !== 'pending') refresh();
    }
  };
  const publish = settled => resource.observers.forEach(observer => observer(owner, settled));
  const mutate = async (kind, id, payload) => {
    const operation = Object.prototype.hasOwnProperty.call(operations, kind) && operations[kind];
    if (!operation || !owns() || resource.write || snapshot.state !== 'available'
      || observedEpoch !== resource.epoch || (!kind.endsWith(':create') && !text(id))) return { kind: 'blocked' };
    const config = authorize();
    if (!config) return { kind: 'blocked' };
    // No caller-owned mutable object is retained across dispatch/acknowledgement.
    const submitted = payload == null ? undefined : JSON.parse(JSON.stringify(payload));
    const attempt = { status: 'pending', kind, id: id || null };
    const life = lifecycle;
    resource.write = attempt;
    ++resource.epoch;
    invalidate();
    patch({ state: 'unavailable', notice: '' });
    publish(false);
    let result;
    try {
      const value = await operation.execute(id, submitted, config);
      // A successful reply that does not match what was sent is its own fault,
      // distinct from a server error: TCA-26102604 reports it as status 200.
      result = operation.valid(value, id, submitted) ? { kind: 'accepted', value }
        : { kind: 'unknown', status: 200, cause: 'server-reply-did-not-match-request', detail: null };
    } catch (error) {
      const status = error?.response?.status;
      const rejected = [400, 401, 403, 404, 409, 422].includes(status);
      const detail = error?.response?.data?.detail;
      result = rejected ? { kind: 'rejected', status, detail } : {
        kind: 'unknown',
        status: Number.isInteger(status) ? status : 0,
        cause: Number.isInteger(status) ? 'unexpected-server-status' : 'no-response',
        detail: typeof detail === 'string' ? detail : null,
      };
    }
    if (resource.write === attempt) {
      // The cause travels with the unknown write so the panel can report it.
      // Nothing here changes which state the write lands in.
      resource.write = result.kind === 'unknown' ? {
        status: 'unknown', kind, id: id || null, submitted: submitted ?? null,
        failure: { status: result.status, cause: result.cause, detail: result.detail },
      } : null;
      ++resource.epoch;
      invalidate();
      publish(true);
    }
    if (!owns() || life !== lifecycle) return { kind: 'retired' };
    const settledEpoch = resource.epoch;
    const acknowledgement = kind === 'source:delete' && result.value?.message === 'Source already deleted'
      ? 'Source is currently absent. This acknowledgement does not establish the effects of an earlier transfer or unassignment.'
      : kind === 'florist:delete' && result.value?.message === 'Already deleted'
        ? 'Florist is currently absent. Earlier mapping-release effects are not established by this acknowledgement.' : 'Change saved.';
    const message = result.kind === 'accepted' ? acknowledgement : result.kind === 'rejected'
      ? 'The change was refused. Review current information before another action.'
      // TCA-26102604: the detail now lives in the popup and its code, so the
      // line left behind says the one thing a person has to act on.
      : 'This change could not be confirmed. Check the list before changing it again.';
    patch({ notice: message });
    const refreshed = await refresh();
    if (!owns() || life !== lifecycle) return { kind: 'retired' };
    if (resource.epoch === settledEpoch && result.kind === 'accepted') {
      patch({ notice: refreshed ? message : `${message} Current information could not be refreshed.` });
    }
    return result;
  };
  return {
    key, owns, read, refresh, mutate,
    subscribe: listener => { listeners.add(listener); return () => listeners.delete(listener); },
    getSnapshot: () => snapshot,
    activate() { active = true; ++lifecycle; resource.observers.add(changed); notify(); if (resource.write?.status !== 'pending') refresh(); },
    retire() { active = false; ++lifecycle; invalidate(); resource.observers.delete(changed); notify(); },
    reviewUnknown() {
      if (!canReview()) return false;
      resource.write = null;
      ++resource.epoch;
      observedEpoch = resource.epoch;
      publish(true);
      patch({ notice: 'You have seen what is stored. Any change you make now is a new change, not a repeat of the one that could not be confirmed.' });
      return true;
    },
  };
}

export function useSourcesCatalog() {
  const { sessionBinding, getToken } = useAuth() || {};
  const revision = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  const principal = identity(sessionBinding);
  const source = connection();
  const roles = sessionBinding?.user?.roles || [sessionBinding?.user?.role];
  const transportCurrent = transportMatches();
  const permitted = roles.includes('admin') && sessionBinding?.source === 'cloud' && !isDesktopOffline() && transportCurrent;
  const latest = useRef(null);
  latest.current = { sessionBinding, getToken, source };
  const controller = useMemo(() => {
    const authorize = () => {
      const current = latest.current;
      const binding = current.sessionBinding;
      const currentRoles = binding?.user?.roles || [binding?.user?.role];
      if (!permitted || !binding?.user?.tenant_id || !binding?.user?.id || binding.source !== 'cloud'
        || identity(binding) !== principal || binding.revision !== revision || getActiveSessionRevision() !== revision
        || !currentRoles.includes('admin') || current.source !== source || connection() !== source || isDesktopOffline() || !transportMatches()) return null;
      const token = current.getToken?.();
      return token ? { headers: { Authorization: `Bearer ${token}` }, timeout: 30000, suppressServerErrorNotification: true } : null;
    };
    return createSourcesCatalogController(authorize, JSON.stringify([sessionBinding?.user?.tenant_id, API]));
  }, [principal, revision, source, permitted, sessionBinding?.user?.tenant_id]);
  useLayoutEffect(() => { controller.activate(); return () => controller.retire(); }, [controller]);
  const snapshot = useSyncExternalStore(controller.subscribe, controller.getSnapshot);
  return { controller, ...snapshot, ...(!transportCurrent ? { notice: 'The backend connection changed. Reload before managing Sources & Senders.' } : {}) };
}
